import React, { useState, useRef, useCallback } from 'react';
import { Lock, Unlock, Copy, Check, GripVertical } from '../../components/icons';
import { getNearestColorName } from '../../utils/colors';
import { hexToRgb } from '../../utils/colors';
import { isLightColor } from '../../utils/studioPalette';

interface StudioColorBarProps {
  hex: string;
  locked: boolean;
  index: number;
  isActive: boolean;
  onToggleLock: () => void;
  onColorChange: (hex: string) => void;
  onSelect: () => void;
  onDragStart: () => void;
  onDragOver: () => void;
  onDrop: () => void;
  isDragOver: boolean;
}

const StudioColorBar: React.FC<StudioColorBarProps> = ({
  hex,
  locked,
  index,
  isActive,
  onToggleLock,
  onColorChange,
  onSelect,
  onDragStart,
  onDragOver,
  onDrop,
  isDragOver,
}) => {
  const [copied, setCopied] = useState(false);
  const pickerRef = useRef<HTMLInputElement>(null);

  const rgb = hexToRgb(hex);
  const colorName = rgb ? getNearestColorName(rgb.r, rgb.g, rgb.b) : 'Unknown';
  const useDark = isLightColor(hex);
  const textClass = useDark ? 'text-black' : 'text-white';
  const btnClass = useDark
    ? 'bg-black/10 hover:bg-black/20 text-black'
    : 'bg-white/15 hover:bg-white/25 text-white';

  const handleCopy = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(hex);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [hex]);

  const handlePickerOpen = useCallback(() => {
    pickerRef.current?.click();
  }, []);

  const handlePickerChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onColorChange(e.target.value.toUpperCase());
  }, [onColorChange]);

  const handleDragStart = useCallback((e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
    onDragStart();
  }, [index, onDragStart]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    onDragOver();
  }, [onDragOver]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    onDrop();
  }, [onDrop]);

  return (
    <div
      className={`
        relative min-w-0 min-h-44 md:min-h-0 flex-1 flex flex-col items-center justify-center gap-2 md:gap-4 px-3 md:px-1
        cursor-pointer select-none transition-all duration-200
        ${isActive ? 'ring-2 ring-inset ring-white/50' : ''}
        ${isDragOver ? 'opacity-70 scale-[0.97]' : ''}
      `}
      style={{ backgroundColor: hex }}
      draggable
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={() => { onSelect(); handlePickerOpen(); }}
      onFocus={onSelect}
      onKeyDown={e => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); e.stopPropagation(); onSelect(); handlePickerOpen(); } }}
      tabIndex={0}
      role="button"
      aria-label={`Color ${index + 1}: ${colorName} (${hex}). Click to edit.`}
    >
      {/* Hidden native color picker */}
      <input
        ref={pickerRef}
        type="color"
        value={hex}
        onChange={handlePickerChange}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
      />

      {/* Lock button */}
      <button
        onClick={e => { e.stopPropagation(); onToggleLock(); }}
        className={`absolute top-3 right-3 flex h-11 w-11 items-center justify-center rounded-control transition-colors ${btnClass}`}
        aria-label={locked ? 'Unlock color' : 'Lock color'}
        style={{ opacity: locked ? 1 : undefined }}
      >
        {locked ? <Lock size={16} /> : <Unlock size={16} />}
      </button>

      {/* Color name */}
      <span className={`max-w-full truncate text-sm font-medium ${textClass} pointer-events-none`}>
        {colorName}
      </span>

      {/* Hex code + copy */}
      <div className="flex md:flex-col lg:flex-row items-center gap-2 pointer-events-auto">
        <span className={`text-base font-mono font-bold ${textClass}`}>
          {hex}
        </span>
        <button
          onClick={handleCopy}
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-control transition-colors ${btnClass}`}
          aria-label="Copy hex code"
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
      </div>

      {/* Drag handle */}
      <div
        className={`absolute bottom-4 left-1/2 -translate-x-1/2 p-2 rounded-full cursor-grab active:cursor-grabbing ${btnClass} opacity-60 hover:opacity-100 transition-opacity`}
        aria-label="Drag to reorder"
      >
        <GripVertical size={16} />
      </div>

      {/* Active indicator */}
      {isActive && (
        <div className={`absolute bottom-14 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full ${useDark ? 'bg-zinc-800' : 'bg-white'}`} />
      )}
    </div>
  );
};

export default StudioColorBar;
